import { TextField, TypographyProps } from '@mui/material';
import { isNotNullOrUndefined } from '@vy/utils';
import React from 'react';
import { TEXTFIELD_WIDTH_PADDING } from './editable-label-const';
import { EditableLabelRenderProps, EditableLabelWrapper } from './EditableLabelWrapper';

type EditableTextLabelProps = {
  label: string;
  onSave: (event: React.FocusEvent<HTMLInputElement>) => Promise<void>;
  displayInline?: boolean;
  labelText?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  fontWeight?: number | string;
  fontSize?: number | string;
} & Omit<TypographyProps, 'fontWeight' | 'fontSize'>;

export const EditableTextLabel: React.FC<EditableTextLabelProps> = ({
  label,
  onSave,
  displayInline = false,
  labelText,
  inputProps,
  fontWeight,
  fontSize,
  ...typographyProps
}) => {
  const handleSave = async (value: string) => {
    const trimmedValue = value.trim();
    const valueHasChanged = trimmedValue !== label;

    const event = {
      target: {
        value: trimmedValue,
      },
    } as React.FocusEvent<HTMLInputElement>;

    if (valueHasChanged && isNotNullOrUndefined(trimmedValue)) {
      await onSave(event);
    }
  };

  const validateValue = (value: string): boolean => {
    return isNotNullOrUndefined(value.trim());
  };

  const renderTextInput = ({ value, onChange, onKeyDown, inputRef }: EditableLabelRenderProps<string>) => {
    const textFieldWidth = value.length + TEXTFIELD_WIDTH_PADDING;

    return (
      <TextField
        inputRef={inputRef as React.RefObject<HTMLInputElement>}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        variant="standard"
        size="small"
        sx={{
          '& .MuiInputBase-input': {
            backgroundColor: '#CFE6FF',
            fontWeight: fontWeight,
            width: `${textFieldWidth}ch`,
            fontSize: fontSize || (typographyProps.variant === 'h4' ? '2.125rem' : undefined),
          },
          '& .MuiInput-underline:before': {
            borderBottom: 'none',
          },
          '& .MuiInput-underline:after': {
            borderBottom: 'none',
          },
          '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottom: 'none',
          },
        }}
        slotProps={{
          htmlInput: inputProps
        }}
      />
    );
  };

  return (
    <EditableLabelWrapper<string>
      label={label}
      value={label}
      onSave={handleSave}
      displayInline={displayInline}
      labelText={labelText}
      renderInput={renderTextInput}
      validateValue={validateValue}
      {...typographyProps}
    />
  );
};
